import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import { useLogger } from 'ui-components/src/hooks/useLogger';

import { UserApplicationRole } from '../../constants';
import { useLoggedIn } from '../../graphql/community/hooks/useLoggedIn';
import { ConfigContext } from '../ConfigContext';
import { ErrorView } from '../../components/ErrorView';
import { LoadingView } from '../../components/LoadingView';
import { NotAuthorizedView } from '../../components/NotAuthorizedView';

const RoleGuardContext = createContext<RoleGuardContextValue | null>(null);

export const useRoleGuardContext = () => {
  const context = useContext(RoleGuardContext);

  if (!context)
    throw new Error(
      'useRoleGuardContext can only be used inside RoleGuardProvider',
    );

  return context;
};

export const RoleGuardProvider: React.FC<RoleGuardProviderProps> = ({
  requiredRoles,
  children,
}) => {
  const logger = useLogger();
  const config = ConfigContext.useContainer();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      try {
        await getCurrentUser();
        if (mounted) setIsAuthenticated(true);
      } catch {
        if (mounted) setIsAuthenticated(false);
      } finally {
        if (mounted) setAuthLoading(false);
      }
    };

    checkAuth();

    return () => {
      mounted = false;
    };
  }, []);

  const { data, loading, error } = useLoggedIn(isAuthenticated);

  useEffect(() => {
    if (error) logger.error(error.message);
  }, [error, logger]);

  const value = useMemo(
    () => ({
      roles: (data?.loggedIn?.roles as UserApplicationRole[]) || [],
      userHasRole: (role: string) => !!data?.loggedIn?.roles?.includes(role),
      userHasRoles: (roles: string[]) =>
        roles.every((role) => data?.loggedIn?.roles?.includes(role)),
    }),
    [data],
  );

  if (authLoading || loading || (!data && !error)) return <LoadingView />;

  if (error)
    return (
      <ErrorView
        onLogout={async () => {
          try {
            await signOut();
            // Amplify v6: manually redirect after signOut
            window.location.href = config.cognito.redirectSignOut;
          } catch (signOutError) {
            logger.error(`Error signing out: ${signOutError}`);
          }
        }}
      />
    );

  if (!isAuthenticated || !value.userHasRoles(requiredRoles))
    return <NotAuthorizedView />;

  return (
    <RoleGuardContext.Provider value={value}>
      {children}
    </RoleGuardContext.Provider>
  );
};

export type RoleGuardContextValue = {
  roles: UserApplicationRole[];
  userHasRole: (role: string) => boolean;
  userHasRoles: (roles: string[]) => boolean;
};

export interface RoleGuardProviderProps {
  requiredRoles: UserApplicationRole[];
  children: React.ReactNode; // ✅ Add this line
}
